/* eslint-disable @typescript-eslint/no-explicit-any */
import { customToast } from "@/components/Common/Toast";
import { fetchEmployeeData } from "@/services/beneficiaries";
import { getAllGoals, insertGoals } from "@/services/goals";
import { RootState } from "@/store";
import { setGoal } from "@/store/wizard";
import { isBoardingProcessingStatue } from "@/types/employeeState";
import { Goal } from "@/types/goals";
import { handleApiError } from "@/utils/errorHandling";
import { calculateAge, formatDate } from "@/utils/generalUtilities";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  calculateMonthlyContribution,
  calculateYearAndAmount,
  recalculateGoalOnPurposeChange,
  recalculateGoalOnYearChange,
} from "../utils/goalCalculations";
import { transformGoalsForAPI } from "../utils/goalTransformers";

import { getWizardConfig } from "@/config/wizardConfig";

const CURRENT_YEAR = new Date().getFullYear();

export const useGoalManagement = () => {
  const dispatch = useDispatch();
  const { id: userAccountID } = useSelector((state: RootState) => state.user);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  const handleFetchGoalsFromAPI = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [beneficiariesResponse, goalsResponse] = await Promise.all([
        fetchEmployeeData(),
        getAllGoals(userAccountID),
      ]);

      const beneficiaries = beneficiariesResponse.data;
      const apiGoals = goalsResponse.data;

      const goalData = beneficiaries
        .filter(
          (item: any) => !isBoardingProcessingStatue(item.onboardingProgress),
        )
        .map((beneficiary: any) => {
          const age = calculateAge(beneficiary.dateOfBirth);

          const existingGoal = apiGoals.find(
            (goal: any) => goal.minorAccountID === beneficiary.minorAccountID,
          );

          if (existingGoal) {
            const goalType = existingGoal.goalType;

            // Prioritize API values when they exist
            const apiAmount = existingGoal.amountRequired;
            const apiYear = existingGoal.timeline
              ? new Date(formatDate(existingGoal.timeline)).getFullYear()
              : null;

            // Only use local calculations as fallback when API values are missing
            const calculation = calculateYearAndAmount(
              beneficiary.dateOfBirth,
              goalType,
            );

            const finalAmount = apiAmount || calculation.amount;
            const finalYear = apiYear || calculation.year;

            // Always calculate projectedAmount based on the year and purpose (never use API amount)
            const projectedAmountCalculation = calculateYearAndAmount(
              beneficiary.dateOfBirth,
              goalType,
            );
            const projectedAmount = apiYear
              ? (() => {
                  const { education } = getWizardConfig();
                  const yearDiff = apiYear - education.baselineYear;
                  const baseAmount =
                    goalType === "CollegeFund"
                      ? education.baseCollegeCost
                      : education.baseTradeschoolCost;
                  return Math.round(
                    yearDiff <= 0
                      ? baseAmount
                      : baseAmount *
                          Math.pow(education.inflationRate, yearDiff),
                  );
                })()
              : projectedAmountCalculation.projectedAmount;

            const monthlyContribution = calculateMonthlyContribution(
              finalAmount,
              finalYear,
            );

            return {
              fullName: beneficiary
                ? `${beneficiary.firstName} ${beneficiary.lastName}`.trim()
                : "",
              beneficiaryId: existingGoal.minorAccountID,
              purpose: goalType || "",
              year: finalYear,
              amount: finalAmount,
              monthlyContribution,
              projectedAmount: projectedAmount,
              age,
              storedYear: apiYear || finalYear,
              dateOfBirth: beneficiary.dateOfBirth,
            };
          }

          return {
            fullName: beneficiary
              ? `${beneficiary.firstName} ${beneficiary.lastName}`.trim()
              : "",
            beneficiaryId: beneficiary.minorAccountID,
            purpose: "",
            year: CURRENT_YEAR,
            amount: 0,
            monthlyContribution: 0,
            age,
            dateOfBirth: beneficiary.dateOfBirth,
          };
        });

      setGoals(goalData);
    } catch (error: any) {
      if (error.statusText) {
        setError(error.statusText);
      } else {
        setError("Unable to fetch beneficiary goals");
      }
    } finally {
      setLoading(false);
    }
  }, [userAccountID]);

  const handleGoalChange = (
    beneficiaryId: number | string,
    field: string,
    value: any,
  ) => {
    setGoals((prev) =>
      prev.map((goal) => {
        if (goal.beneficiaryId === beneficiaryId) {
          const updates = { ...goal, [field]: value } as Goal;

          if (field === "purpose") {
            const recalculated = recalculateGoalOnPurposeChange(goal, value);
            Object.assign(updates, recalculated);
          } else if (field === "year") {
            const recalculated = recalculateGoalOnYearChange(goal, value);
            updates.storedYear = value;
            // Keep projectedAmount from recalculated (it's the average cost for the new year)
            Object.assign(updates, recalculated);
          } else if (field === "amount") {
            updates.monthlyContribution = calculateMonthlyContribution(
              value,
              updates.year,
            );
            // DO NOT update projectedAmount when amount changes - it should remain the original average
          }

          return updates;
        }
        return goal;
      }),
    );
  };

  const processAddOrUpdateGoals = async (validGoals: Goal[]) => {
    try {
      setIsSubmitting(true);
      setIsSuccess(true);

      // Filter out goals with $0 amount before sending to API
      const goalsWithValidAmounts = validGoals.filter(
        (goal) => goal.amount > 0,
      );

      if (goalsWithValidAmounts.length === 0) {
        customToast(
          "No valid goals to process. All goals must have an amount greater than $0.",
          "error",
        );
        setIsSuccess(false);
        return false;
      }

      const formattedGoals = transformGoalsForAPI(goalsWithValidAmounts);
      const response = await insertGoals(formattedGoals, { userAccountID });

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid response format from goal API");
      }

      let success = true;
      response.data.forEach((result, index) => {
        if (result.status === 200) {
          const goal = goalsWithValidAmounts[index];
          customToast(
            `Successfully processed ${goal.fullName}'s goal`,
            "success",
          );
          dispatch(setGoal(goal));
        } else {
          success = false;
          setIsSuccess(false);
          customToast(
            `Failed to process goal for ${goalsWithValidAmounts[index].fullName}`,
            "error",
          );
        }
      });

      return success;
    } catch (error) {
      setIsSuccess(false);
      handleApiError(error, "Failed to process goals", true, customToast);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    goals,
    setGoals,
    loading,
    isSubmitting,
    error,
    isSuccess,
    handleFetchGoalsFromAPI,
    handleGoalChange,
    processAddOrUpdateGoals,
  };
};
